/*! @azure/msal-browser v5.19.0 2026-08-18 */
'use strict';
import { createClientAuthError, ClientAuthErrorCodes, JsonWebTokenAlgorithms } from '@azure/msal-common/browser';
import { CryptoOptsGetPublicKeyThumbprint } from '../telemetry/BrowserPerformanceEvents.mjs';
import { exportJwk, generateKeyPair, computeJwkThumbprint, RSA_KEYGEN_ALGORITHM_OPTIONS, ECDSA_P256_KEYGEN_ALGORITHM_OPTIONS } from './BrowserCrypto.mjs';
import { createBrowserAuthError } from '../error/BrowserAuthError.mjs';
import { AsyncMemoryStorage } from '../cache/AsyncMemoryStorage.mjs';
import { cryptoKeyNotFound, unsupportedTokenBindingAlgorithm } from '../error/BrowserAuthErrorCodes.mjs';

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
/**
 * Owns browser token-binding key lifecycle and storage lookup.
 * @internal
 */
class TokenBindingKeyManager {
    constructor(logger, performanceClient) {
        this.logger = logger;
        this.cache = TokenBindingKeyManager.getTokenBindingKeyStorage(this.logger);
        this.performanceClient = performanceClient;
    }
    static getTokenBindingKeyStorage(logger) {
        if (!TokenBindingKeyManager.tokenBindingKeyStorage) {
            TokenBindingKeyManager.tokenBindingKeyStorage =
                new AsyncMemoryStorage(logger);
        }
        return TokenBindingKeyManager.tokenBindingKeyStorage;
    }
    /**
     * Provisions a browser token-binding key and returns its key identifier.
     * @param request - Key provisioning policy.
     */
    async provisionTokenBindingKey(request) {
        const publicKeyThumbMeasurement = this.performanceClient?.startMeasurement(CryptoOptsGetPublicKeyThumbprint, request.correlationId);
        try {
            const activeKeyPair = await this.createTokenBindingKey(request);
            publicKeyThumbMeasurement?.end({
                success: true,
                ...this.getTokenBindingKeyTelemetry(activeKeyPair),
            });
            return activeKeyPair.keyId;
        }
        catch (e) {
            publicKeyThumbMeasurement?.end({
                success: false,
                tokenBindingKeyType: request.tokenBindingKeyType,
                tokenBindingKeyAlgorithm: request.tokenBindingKeyAlgorithm,
            });
            throw e;
        }
    }
    /**
     * Removes a browser token-binding key by identifier.
     * @param kid - Token-binding key identifier.
     * @param correlationId - Request correlation identifier.
     */
    async removeTokenBindingKey(kid, correlationId) {
        await this.cache.removeItem(kid, correlationId);
        const keyFound = await this.cache.containsKey(kid, correlationId);
        if (keyFound) {
            throw createClientAuthError(ClientAuthErrorCodes.bindingKeyNotRemoved, correlationId);
        }
    }
    /**
     * Clears browser token-binding keys from memory and persistent storage.
     * @param correlationId - Request correlation identifier.
     */
    async clearKeystore(correlationId) {
        this.cache.clearInMemory(correlationId);
        try {
            await this.cache.clearPersistent(correlationId);
            return true;
        }
        catch (e) {
            if (e instanceof Error) {
                this.logger.error(`1owpn8 ${e.message}`, correlationId);
            }
            else {
                this.logger.error("0yrmwo", correlationId);
            }
            return false;
        }
    }
    /**
     * Gets a token-binding public key as a JWK by identifier.
     * @param keyId - Token-binding key identifier.
     * @param correlationId - Request correlation identifier.
     */
    async getTokenBindingPublicKeyJwk(keyId, correlationId) {
        const cachedKeyPair = await this.getTokenBindingKeyPair(keyId, correlationId);
        return exportJwk(cachedKeyPair.publicKey);
    }
    async createTokenBindingKey(request) {
        const keyGenAlgorithm = this.getTokenBindingKeyGenAlgorithmOptions(request.tokenBindingKeyAlgorithm, request.correlationId);
        const generatedKeyPair = await this.generateKeyPairAndThumbprint(TokenBindingKeyManager.TOKEN_BINDING_KEY_USAGES, keyGenAlgorithm, request.correlationId);
        await this.cache.setItem(generatedKeyPair.keyId, {
            ...generatedKeyPair,
            tokenBindingKeyType: request.tokenBindingKeyType,
            tokenBindingKeyAlgorithm: request.tokenBindingKeyAlgorithm,
        }, request.correlationId);
        return {
            ...generatedKeyPair,
            tokenBindingKeyType: request.tokenBindingKeyType,
            tokenBindingKeyAlgorithm: request.tokenBindingKeyAlgorithm,
        };
    }
    async generateKeyPairAndThumbprint(usages, keyGenAlgorithm, correlationId) {
        const keyPair = await generateKeyPair(false, usages, keyGenAlgorithm);
        const publicJwk = await exportJwk(keyPair.publicKey);
        const keyId = await computeJwkThumbprint(publicJwk, correlationId);
        return {
            publicKey: keyPair.publicKey,
            privateKey: keyPair.privateKey,
            keyId,
        };
    }
    /** @internal */
    async getTokenBindingKeyPair(keyId, correlationId) {
        const cachedKeyPair = await this.cache.getItem(keyId, correlationId);
        if (!cachedKeyPair) {
            throw createBrowserAuthError(cryptoKeyNotFound, correlationId);
        }
        return cachedKeyPair;
    }
    /** @internal */
    getTokenBindingKeyTelemetry(cachedKeyPair, fallbackAlgorithm) {
        return {
            ...(cachedKeyPair.tokenBindingKeyType && {
                tokenBindingKeyType: cachedKeyPair.tokenBindingKeyType,
            }),
            ...(cachedKeyPair.tokenBindingKeyAlgorithm || fallbackAlgorithm
                ? {
                    tokenBindingKeyAlgorithm: cachedKeyPair.tokenBindingKeyAlgorithm ||
                        fallbackAlgorithm,
                }
                : {}),
        };
    }
    getTokenBindingKeyGenAlgorithmOptions(tokenBindingKeyAlgorithm, correlationId) {
        if (tokenBindingKeyAlgorithm === JsonWebTokenAlgorithms.RS256) {
            return RSA_KEYGEN_ALGORITHM_OPTIONS;
        }
        if (tokenBindingKeyAlgorithm === JsonWebTokenAlgorithms.ES256) {
            return ECDSA_P256_KEYGEN_ALGORITHM_OPTIONS;
        }
        throw createBrowserAuthError(unsupportedTokenBindingAlgorithm, correlationId);
    }
}
TokenBindingKeyManager.TOKEN_BINDING_KEY_USAGES = [
    "sign",
    "verify",
];

export { TokenBindingKeyManager };
//# sourceMappingURL=TokenBindingKeyManager.mjs.map
